/**
 * KUFavoriteHeart — Bouton ♥ favori pour une KnowledgeUnit.
 *
 * Vincent B614 (Eric 2026-07-29 SPEC STRICK) — Le ♥ favori apparaît
 * désormais sur chaque connaissance (Q&R, Script, Procédure, Objection,
 * Concept) dans l'onglet « Modifier » de l'Explorateur de fichiers (et
 * partout où on liste des KUs). Auparavant l'utilisateur ne pouvait
 * mettre en favori une connaissance QUE depuis [Base de connaissances].
 *
 * Comportement :
 *  - Lit `initialIsFavorite` (fourni par le parent qui charge la liste
 *    favoris via `/api/favorites`) puis maintient son propre état local
 *    pour la bascule optimiste (setter immédiat + rollback si erreur).
 *  - À la bascule : POST/DELETE `/api/favorites` avec `type: "knowledge"`,
 *    puis émission de `window.dispatchEvent("favorites:updated")` (interceptor
 *    axios AuthContext le fait déjà, on garde ceci pour la version fetch).
 *  - Écoute `favorites:updated` pour se re-sync si le favori est basculé
 *    depuis une autre page (Dashboard, Base de connaissances).
 */
import { useEffect, useState } from "react";
import { Heart } from "@phosphor-icons/react";
import axios from "axios";
import { API_BASE as API } from "../../lib/apiBase";
import { cachedGet, invalidateCache } from "../../lib/apiCache";

export default function KUFavoriteHeart({
  unitId,
  initialIsFavorite = false,
  size = 14,
  className = "",
  testIdPrefix = "ku-fav",
  onChange,
}) {
  const [isFav, setIsFav] = useState(Boolean(initialIsFavorite));

  useEffect(() => {
    setIsFav(Boolean(initialIsFavorite));
  }, [initialIsFavorite]);

  useEffect(() => {
    // Vincent B614 — Re-sync automatique quand un autre écran modifie les
    // favoris de l'utilisateur courant. Léger (requête filtrée par
    // item_id) pour ne pas alourdir le rendu.
    const onFavUpdated = async () => {
      try {
        const { data } = await cachedGet(axios, `${API}/api/favorites`, { ttl: 30000 });
        const list = Array.isArray(data) ? data : [];
        const found = list.some(
          (f) => f.item_type === "knowledge" && f.item_id === unitId
        );
        setIsFav(found);
      } catch (_) { /* silencieux : on garde l'état local */ }
    };
    window.addEventListener("favorites:updated", onFavUpdated);
    return () => window.removeEventListener("favorites:updated", onFavUpdated);
  }, [unitId]);

  const toggle = async (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    const wasFav = isFav;
    setIsFav(!wasFav); // optimistic
    try {
      if (wasFav) {
        await axios.delete(`${API}/api/favorites/knowledge/${unitId}`, { withCredentials: true });
      } else {
        await axios.post(
          `${API}/api/favorites`,
          { type: "knowledge", item_id: unitId },
          { withCredentials: true }
        );
      }
      invalidateCache("/api/favorites");
      onChange?.(!wasFav);
    } catch (_err) {
      setIsFav(wasFav); // rollback
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      className={`p-1 rounded transition-colors flex-shrink-0 ${
        isFav ? "text-rose-500 hover:text-rose-600" : "text-zinc-300 hover:text-rose-400"
      } ${className}`}
      data-testid={`${testIdPrefix}-${unitId}`}
      title={isFav ? "Retirer des favoris" : "Ajouter aux favoris"}
      aria-label={isFav ? "Retirer des favoris" : "Ajouter aux favoris"}
      aria-pressed={isFav}
    >
      <Heart size={size} weight={isFav ? "fill" : "regular"} />
    </button>
  );
}
